import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Plus, Eye, Edit, Copy, Trash2, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown,
  Tag, AlertTriangle, Clock, CheckCircle, Bell, BellOff
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import ExportButton from '../../components/ExportButton';
import { toast } from 'sonner';
import SelectWithCreate from '../../components/ui/select-with-create';
import { formatDateOnlyARG } from '../../lib/utils';

const Licencias = () => {
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('assets', 'licencias');
  const [licencias, setLicencias] = useState([]);
  const [stats, setStats] = useState({ total: 0, vencidas: 0, prox_30: 0, prox_90: 0, vigentes: 0, sin_alerta: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', empresa_id: '', fabricante_id: '', tipo_licencia_id: '' });
  const [searchInput, setSearchInput] = useState(''); // Input inmediato para debounce
  const [sortConfig, setSortConfig] = useState({ field: 'licencia', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [pageSize, setPageSize] = useState(25);

  // Form options
  const [options, setOptions] = useState({ 
    empresas: [], sites: [], fabricantes: [], proveedores: [], 
    tipos_licencia: [], tipos_vencimiento: [], tiempos_alerta: [] 
  });

  // Filter options (only values that exist in licencias table)
  const [filterOptions, setFilterOptions] = useState({ 
    empresas: [], fabricantes: [], tipos_licencia: [] 
  });

  // Modals
  const [viewModal, setViewModal] = useState({ open: false, licencia: null });
  const [editModal, setEditModal] = useState({ open: false, licencia: null, isCopy: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, licencia: null });
  const [formData, setFormData] = useState({
    licencia: '', nro_serie: '', cantidad: '', id_empresa: '', sitio: '',
    id_fabricante: '', id_proveedor: '', id_tipo_licencia: '', id_tipo_vencimiento: '',
    vencimiento: '', debe_alertar: 0, tiempo_alerta: '', descripcion: '', observaciones: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/licencias/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await api.get('/assets/licencias/form/options');
      setOptions(response.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/licencias/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchLicencias = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.fabricante_id) params.append('fabricante_id', filters.fabricante_id);
      if (filters.tipo_licencia_id) params.append('tipo_licencia_id', filters.tipo_licencia_id);
      
      // Filtro de vencimiento desde stats cards
      if (activeStatFilter !== 'all') {
        params.append('vencimiento_filter', activeStatFilter);
      }

      const response = await api.get(`/assets/licencias?${params}`);
      setLicencias(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar licencias');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOptions();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchLicencias();
  }, [page, pageSize, filters.search, filters.empresa_id, filters.fabricante_id, filters.tipo_licencia_id, sortConfig, activeStatFilter]);

  // Debounce para filtro de búsqueda (800ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
        setPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // Stat card click handler
  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    setPage(1);
    setSearchInput('');
    // Reset inline filters when clicking stat cards
    setFilters({ search: '', empresa_id: '', fabricante_id: '', tipo_licencia_id: '' });
  };

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Vencimiento badge
  const getVencimientoBadge = (licencia) => {
    if (!licencia.debe_alertar) return <span className="badge badge-outline">Sin alerta</span>;
    
    const status = licencia.vencimiento_status;
    if (status === 'vencida') return <span className="badge badge-danger">Vencida</span>;
    if (status === 'prox_30') return <span className="badge badge-warning">Prox. 30d</span>;
    if (status === 'prox_90') return <span className="badge badge-info">Prox. 90d</span>;
    if (status === 'vigente') return <span className="badge badge-success">Vigente</span>;
    return <span className="badge badge-outline">-</span>;
  };

  // Format date
  const formatDate = (dateStr) => formatDateOnlyARG(dateStr);

  // View modal
  const handleView = (licencia) => {
    setViewModal({ open: true, licencia });
  };

  // Edit modal
  const handleEdit = (licencia) => {
    // Extraer solo la parte de fecha si hay datetime completo
    let vencimientoDate = '';
    if (licencia.vencimiento) {
      // Si viene como ISO string con tiempo, extraer solo YYYY-MM-DD
      vencimientoDate = licencia.vencimiento.split('T')[0];
    }
    
    setFormData({
      licencia: licencia.licencia || '',
      nro_serie: licencia.nro_serie || '',
      cantidad: licencia.cantidad?.toString() || '',
      id_empresa: licencia.empresa_id?.toString() || '',
      sitio: licencia.site_id?.toString() || '',
      id_fabricante: licencia.fabricante_id?.toString() || '',
      id_proveedor: licencia.proveedor_id?.toString() || '',
      id_tipo_licencia: licencia.tipo_licencia_id?.toString() || '',
      id_tipo_vencimiento: licencia.tipo_vencimiento_id?.toString() || '',
      vencimiento: vencimientoDate,
      debe_alertar: licencia.debe_alertar ? 1 : 0,
      tiempo_alerta: licencia.tiempo_alerta_id?.toString() || '',
      descripcion: licencia.descripcion || '',
      observaciones: licencia.observaciones || ''
    });
    setEditModal({ open: true, licencia, isCopy: false });
  };

  // Copy modal
  const handleCopy = (licencia) => {
    setFormData({
      licencia: '', // Unique - clear
      nro_serie: '', // Unique - clear
      cantidad: licencia.cantidad?.toString() || '',
      id_empresa: licencia.empresa_id?.toString() || '',
      sitio: licencia.site_id?.toString() || '',
      id_fabricante: licencia.fabricante_id?.toString() || '',
      id_proveedor: licencia.proveedor_id?.toString() || '',
      id_tipo_licencia: licencia.tipo_licencia_id?.toString() || '',
      id_tipo_vencimiento: licencia.tipo_vencimiento_id?.toString() || '',
      vencimiento: '', // Clear for new entry
      debe_alertar: licencia.debe_alertar ? 1 : 0,
      tiempo_alerta: licencia.tiempo_alerta_id?.toString() || '',
      descripcion: licencia.descripcion || '',
      observaciones: licencia.observaciones || ''
    });
    setEditModal({ open: true, licencia: null, isCopy: true });
  };

  // Create new
  const handleCreate = () => {
    setFormData({
      licencia: '', nro_serie: '', cantidad: '', id_empresa: '', sitio: '',
      id_fabricante: '', id_proveedor: '', id_tipo_licencia: '', id_tipo_vencimiento: '',
      vencimiento: '', debe_alertar: 0, tiempo_alerta: '', descripcion: '', observaciones: ''
    });
    setEditModal({ open: true, licencia: null, isCopy: false });
  };

  // Save
  const handleSave = async () => {
    if (!formData.licencia.trim()) {
      toast.error('El nombre de la licencia es requerido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        licencia: formData.licencia.trim(),
        nro_serie: formData.nro_serie || null,
        cantidad: formData.cantidad ? parseInt(formData.cantidad) : null,
        id_empresa: formData.id_empresa ? parseInt(formData.id_empresa) : null,
        sitio: formData.sitio ? parseInt(formData.sitio) : null,
        id_fabricante: formData.id_fabricante ? parseInt(formData.id_fabricante) : null,
        id_proveedor: formData.id_proveedor ? parseInt(formData.id_proveedor) : null,
        id_tipo_licencia: formData.id_tipo_licencia ? parseInt(formData.id_tipo_licencia) : null,
        id_tipo_vencimiento: formData.id_tipo_vencimiento ? parseInt(formData.id_tipo_vencimiento) : null,
        vencimiento: formData.vencimiento && formData.vencimiento.trim() !== '' ? formData.vencimiento : null,
        debe_alertar: formData.debe_alertar,
        tiempo_alerta: formData.tiempo_alerta ? parseInt(formData.tiempo_alerta) : null,
        descripcion: formData.descripcion || null,
        observaciones: formData.observaciones || null
      };

      if (editModal.licencia && !editModal.isCopy) {
        await api.put(`/assets/licencias/${editModal.licencia.id}`, payload);
        toast.success('Licencia actualizada exitosamente');
      } else {
        await api.post('/assets/licencias', payload);
        toast.success('Licencia creada exitosamente');
      }
      setEditModal({ open: false, licencia: null, isCopy: false });
      fetchLicencias();
      fetchStats();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        // Error de validación de Pydantic
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await api.delete(`/assets/licencias/${deleteModal.licencia.id}`);
      toast.success('Licencia eliminada exitosamente');
      setDeleteModal({ open: false, licencia: null });
      fetchLicencias();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Toggle alerta inline
  const handleToggleAlerta = async (id, checked) => {
    try {
      await api.patch(`/assets/licencias/${id}/toggle-alerta`, { debe_alertar: checked ? 1 : 0 });
      // Update local state without refetching
      setLicencias(prev => prev.map(lic => 
        lic.id === id ? { ...lic, debe_alertar: checked } : lic
      ));
      fetchStats(); // Refresh stats only
      toast.success(checked ? 'Alerta activada' : 'Alerta desactivada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  // Quick create handlers
  const handleQuickCreateEmpresa = async (nombre, descripcion) => {
    const response = await api.post('/assets/licencias/quick-create/empresa', { nombre, descripcion });
    await fetchOptions();
    return response.data;
  };

  const handleQuickCreateSite = async (nombre, descripcion) => {
    const response = await api.post('/assets/licencias/quick-create/site', { nombre, descripcion });
    await fetchOptions();
    return response.data;
  };

  const handleQuickCreateFabricante = async (nombre) => {
    const response = await api.post('/assets/licencias/quick-create/fabricante', { nombre });
    await fetchOptions();
    return response.data;
  };

  const handleQuickCreateProveedor = async (nombre) => {
    const response = await api.post('/assets/licencias/quick-create/proveedor', { nombre });
    await fetchOptions();
    return response.data;
  };

  const handleQuickCreateTipoLicencia = async (nombre) => {
    const response = await api.post('/assets/licencias/quick-create/tipo-licencia', { nombre });
    await fetchOptions();
    return response.data;
  };

  const handleQuickCreateTipoVencimiento = async (nombre) => {
    const response = await api.post('/assets/licencias/quick-create/tipo-vencimiento', { nombre });
    await fetchOptions();
    return response.data;
  };

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver Licencias</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Licencias</h1>
            <p className="page-subtitle">Gestion de licencias de software y sus vencimientos</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/licencias"
              filters={{ search: filters.search }}
              filename="licencias"
            />
            {canCreate && (
              <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-licencia-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Licencia
              </Button>
            )}
          </div>
        </div>

        {/* Stats - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="licencias-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <Tag className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-red-500/50 ${activeStatFilter === 'vencidas' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => handleStatFilter('vencidas')}
            data-testid="stats-vencidas"
          >
            <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-red-500">{stats.vencidas}</div>
              <div className="stats-card-label">Vencidas</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-amber-500/50 ${activeStatFilter === 'prox_30' ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => handleStatFilter('prox_30')}
            data-testid="stats-prox30"
          >
            <Clock className="h-6 w-6 text-amber-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-amber-500">{stats.prox_30}</div>
              <div className="stats-card-label">Prox. 30d</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-blue-500/50 ${activeStatFilter === 'prox_90' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => handleStatFilter('prox_90')}
            data-testid="stats-prox90"
          >
            <Clock className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">{stats.prox_90}</div>
              <div className="stats-card-label">Prox. 90d</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${activeStatFilter === 'vigentes' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatFilter('vigentes')}
            data-testid="stats-vigentes"
          >
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.vigentes}</div>
              <div className="stats-card-label">Vigentes</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-gray-500/50 ${activeStatFilter === 'sin_alerta' ? 'ring-2 ring-gray-500' : ''}`}
            onClick={() => handleStatFilter('sin_alerta')}
            data-testid="stats-sin-alerta"
          >
            <BellOff className="h-6 w-6 text-gray-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-gray-500">{stats.sin_alerta}</div>
              <div className="stats-card-label">Sin Alerta</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('licencia')}
                        >
                          <div className="flex items-center gap-1">
                            LICENCIA
                            <SortIcon field="licencia" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('empresa')}
                        >
                          <div className="flex items-center gap-1">
                            EMPRESA
                            <SortIcon field="empresa" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('fabricante')}
                        >
                          <div className="flex items-center gap-1">
                            FABRICANTE
                            <SortIcon field="fabricante" />
                          </div>
                        </th>
                        <th>TIPO</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('vencimiento')}
                        >
                          <div className="flex items-center gap-1">
                            VENCIMIENTO
                            <SortIcon field="vencimiento" />
                          </div>
                        </th>
                        <th className="text-center">ESTADO</th>
                        <th className="text-center">ALERTA</th>
                        <th className="text-center">CANT.</th>
                        <th className="w-32 text-center">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar en todos los campos..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.empresa_id || ""}
                            onChange={(e) => handleFilterChange('empresa_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todas</option>
                            {filterOptions.empresas?.map(e => (
                              <option key={e.id} value={e.id}>{e.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.fabricante_id || ""}
                            onChange={(e) => handleFilterChange('fabricante_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todos</option>
                            {filterOptions.fabricantes?.map(f => (
                              <option key={f.id} value={f.id}>{f.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.tipo_licencia_id || ""}
                            onChange={(e) => handleFilterChange('tipo_licencia_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todos</option>
                            {filterOptions.tipos_licencia?.map(t => (
                              <option key={t.id} value={t.id}>{t.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="w-32"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {licencias.length === 0 ? (
                        <tr>
                          <td colSpan={9}>
                            <div className="empty-state">
                              <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron licencias</p>
                            </div>
                          </td>
                        </tr>
                      ) : licencias.map((licencia) => (
                        <tr key={licencia.id} data-testid={`licencia-row-${licencia.id}`}>
                          <td>
                            <span className="font-medium text-primary">{licencia.licencia}</span>
                          </td>
                          <td className="text-muted-foreground">{licencia.empresa_nombre || '-'}</td>
                          <td className="text-muted-foreground">{licencia.fabricante_nombre || '-'}</td>
                          <td>
                            {licencia.tipo_licencia_nombre ? (
                              <span className="badge badge-outline">{licencia.tipo_licencia_nombre}</span>
                            ) : '-'}
                          </td>
                          <td className="font-mono text-sm">{formatDate(licencia.vencimiento)}</td>
                          <td className="text-center">{getVencimientoBadge(licencia)}</td>
                          <td className="text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Switch
                                checked={licencia.debe_alertar}
                                onCheckedChange={(checked) => handleToggleAlerta(licencia.id, checked)}
                                disabled={!canUpdate}
                                className="data-[state=checked]:bg-primary"
                                data-testid={`switch-alerta-${licencia.id}`}
                              />
                              <span className="text-xs text-muted-foreground">
                                {licencia.debe_alertar ? 'Si' : 'No'}
                              </span>
                            </div>
                          </td>
                          <td className="text-center text-muted-foreground">{licencia.cantidad || '-'}</td>
                          <td className="w-32">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => handleView(licencia)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-licencia-${licencia.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canUpdate && handleEdit(licencia)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-licencia-${licencia.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canCreate && handleCopy(licencia)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50' : ''}`}
                                disabled={!canCreate}
                                title="Copiar"
                                data-testid={`copy-licencia-${licencia.id}`}
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, licencia })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-licencia-${licencia.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      {total > 0 ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} de ${total}` : '0 registros'}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pageSize.toString()}
                        onValueChange={(value) => { setPageSize(parseInt(value)); setPage(1); }}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* View Modal */}
        <Dialog open={viewModal.open} onOpenChange={(open) => setViewModal({ open, licencia: null })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-primary" />
                {viewModal.licencia?.licencia}
              </DialogTitle>
            </DialogHeader>
            {viewModal.licencia && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Licencia</Label>
                    <p className="font-medium">{viewModal.licencia.licencia}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Nro Serie</Label>
                    <p className="font-mono text-sm">{viewModal.licencia.nro_serie || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Empresa</Label>
                    <p>{viewModal.licencia.empresa_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Site</Label>
                    <p>{viewModal.licencia.site_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Fabricante</Label>
                    <p>{viewModal.licencia.fabricante_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Proveedor</Label>
                    <p>{viewModal.licencia.proveedor_nombre || '-'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Tipo Licencia</Label>
                    <p>{viewModal.licencia.tipo_licencia_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Tipo Vencimiento</Label>
                    <p>{viewModal.licencia.tipo_vencimiento_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Vencimiento</Label>
                    <p>{formatDate(viewModal.licencia.vencimiento)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Estado</Label>
                    <div className="mt-1">{getVencimientoBadge(viewModal.licencia)}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Cantidad</Label>
                    <p>{viewModal.licencia.cantidad || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Alerta</Label>
                    <p>{viewModal.licencia.debe_alertar ? `Si - ${viewModal.licencia.tiempo_alerta_nombre || 'Sin configurar'}` : 'No'}</p>
                  </div>
                </div>
                <div className="col-span-2 space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Descripcion</Label>
                    <p className="text-sm">{viewModal.licencia.descripcion || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Observaciones</Label>
                    <p className="text-sm text-muted-foreground">{viewModal.licencia.observaciones || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit/Create/Copy Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, licencia: null, isCopy: false })}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-primary" />
                {editModal.isCopy ? 'Copiar Licencia' : editModal.licencia ? 'Editar Licencia' : 'Nueva Licencia'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-3 gap-4">
              {/* Row 1 - Identificacion */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Licencia *</Label>
                <Input
                  value={formData.licencia}
                  onChange={(e) => setFormData(prev => ({ ...prev, licencia: e.target.value }))}
                  placeholder="Nombre de la licencia"
                  data-testid="input-licencia"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Nro Serie</Label>
                <Input
                  value={formData.nro_serie}
                  onChange={(e) => setFormData(prev => ({ ...prev, nro_serie: e.target.value }))}
                  placeholder="Serial o clave"
                  data-testid="input-serie"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Cantidad</Label>
                <Input
                  type="number"
                  value={formData.cantidad}
                  onChange={(e) => setFormData(prev => ({ ...prev, cantidad: e.target.value }))}
                  placeholder="1"
                  data-testid="input-cantidad"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* Row 2 - Organizacion */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Empresa</Label>
                <SelectWithCreate
                  value={formData.id_empresa}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, id_empresa: v }))}
                  options={options.empresas}
                  placeholder="Seleccionar empresa"
                  onCreate={handleQuickCreateEmpresa}
                  createLabel="empresa"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Site</Label>
                <SelectWithCreate
                  value={formData.sitio}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, sitio: v }))}
                  options={options.sites}
                  placeholder="Seleccionar site"
                  onCreate={handleQuickCreateSite}
                  createLabel="site"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Fabricante</Label>
                <SelectWithCreate
                  value={formData.id_fabricante}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, id_fabricante: v }))}
                  options={options.fabricantes}
                  placeholder="Seleccionar fabricante"
                  onCreate={handleQuickCreateFabricante}
                  createLabel="fabricante"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* Row 3 - Terceros y Tipos */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Proveedor</Label>
                <SelectWithCreate
                  value={formData.id_proveedor}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, id_proveedor: v }))}
                  options={options.proveedores}
                  placeholder="Seleccionar proveedor"
                  onCreate={handleQuickCreateProveedor}
                  createLabel="proveedor"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Tipo Licencia</Label>
                <SelectWithCreate
                  value={formData.id_tipo_licencia}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, id_tipo_licencia: v }))}
                  options={options.tipos_licencia}
                  placeholder="Seleccionar tipo"
                  onCreate={handleQuickCreateTipoLicencia}
                  createLabel="tipo"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Tipo Vencimiento</Label>
                <SelectWithCreate
                  value={formData.id_tipo_vencimiento}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, id_tipo_vencimiento: v }))}
                  options={options.tipos_vencimiento}
                  placeholder="Seleccionar tipo"
                  onCreate={handleQuickCreateTipoVencimiento}
                  createLabel="tipo"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* Row 4 - Vencimiento y Alertas */}
              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-yellow-500/50 rounded-md p-2 bg-yellow-500/5' : ''}`}>
                <Label className="text-xs">Fecha Vencimiento</Label>
                <Input
                  type="date"
                  value={formData.vencimiento}
                  onChange={(e) => setFormData(prev => ({ ...prev, vencimiento: e.target.value }))}
                  data-testid="input-vencimiento"
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Debe Alertar</Label>
                <div className="flex items-center gap-2 h-9">
                  <Switch
                    checked={formData.debe_alertar === 1}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, debe_alertar: checked ? 1 : 0 }))}
                    data-testid="switch-alertar"
                  />
                  <span className="text-sm text-muted-foreground">
                    {formData.debe_alertar === 1 ? 'Si' : 'No'}
                  </span>
                </div>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              <div className={`space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Tiempo Alerta</Label>
                <select
                  value={formData.tiempo_alerta || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, tiempo_alerta: e.target.value }))}
                  className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  disabled={formData.debe_alertar !== 1}
                >
                  <option value="">Seleccionar...</option>
                  {options.tiempos_alerta.map(t => (
                    <option key={t.id} value={t.id}>{t.nombre} ({t.dias} dias)</option>
                  ))}
                </select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* Row 5 - Descripcion y Observaciones */}
              <div className={`col-span-3 space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Descripcion</Label>
                <Input
                  value={formData.descripcion}
                  onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Descripcion de la licencia"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className={`col-span-3 space-y-1 ${editModal.isCopy ? 'ring-2 ring-green-500/50 rounded-md p-2 bg-green-500/5' : ''}`}>
                <Label className="text-xs">Observaciones</Label>
                <Textarea
                  value={formData.observaciones}
                  onChange={(e) => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
                  placeholder="Notas adicionales..."
                  rows={2}
                  data-testid="input-observations"
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, licencia: null, isCopy: false })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, licencia: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Licencia
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Estas seguro que deseas eliminar la licencia <strong>{deleteModal.licencia?.licencia}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, licencia: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Licencias;
